import { useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { LoadingButton } from "@atlaskit/button";
import { FormFooter } from "@atlaskit/form";
import Heading from "@atlaskit/heading";
import { Box, Flex, Stack, xcss } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { experiments, useExperimentExposure } from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { PageBoardColumnsLeftProps } from "../../../common/types.tsx";
import { useIsJFindInviteStepExperiment } from "../../../hooks/experiment-hooks.ts";

import messages from "./messages.tsx";
import { ColumnInputs, onClickFinishButton } from "./utils.tsx";

export const LeftHandSidePageBoardColumns = ({
    onClickNext,
    columnNames,
    setColumnNames,
    isBoardColumnsSubmitting
}: PageBoardColumnsLeftProps) => {
    const [emptyInputIndexes, setEmptyInputIndexes] = useState<number[]>([]);
    const [dupeIndexes, setDupeIndexes] = useState<number[]>([]);
    const [exceededInputLengthIndexes, setExceededInputLengthIndexes] =
        useState<number[]>([]);
    const isJfind1663ExperimentEnabled = useIsJFindInviteStepExperiment();
    const modalVisualUpdates = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    );

    // FIRE EXPOSURE FOR JFIND-1663 - first visible UX change is the CTA content on this page
    useExperimentExposure(experiments.jfindOnboardingWizardInviteStep);

    const onClickNextWrapper = ({ columnNames }: { columnNames: string[] }) => {
        onClickNext({ columnNames });
    };

    const buttonMsg = isJfind1663ExperimentEnabled ? (
        <FormattedMessage {...messages.nextButtonCta} />
    ) : (
        <FormattedMessage {...messages.finishButtonCta} />
    );

    return (
        <>
            <Stack space="space.200">
                <div css={[modalVisualUpdates && titleVisualUpdateCss]}>
                    <Heading size="xlarge" as="h1">
                        <FormattedMessage {...messages.title} />
                    </Heading>
                </div>
                {modalVisualUpdates ? (
                    <p css={subtextVisualUpdateStyles}>
                        <FormattedMessage {...messages.subtitle} />
                    </p>
                ) : (
                    <Box xcss={subtextStyles}>
                        <FormattedMessage {...messages.subtitle} />
                    </Box>
                )}
                <ColumnInputs
                    columnNames={columnNames}
                    setColumnNames={setColumnNames}
                    emptyInputIndexes={emptyInputIndexes}
                    setEmptyInputIndexes={setEmptyInputIndexes}
                    dupeIndexes={dupeIndexes}
                    setDupeIndexes={setDupeIndexes}
                    exceededInputLengthIndexes={exceededInputLengthIndexes}
                    setExceededInputLengthIndexes={
                        setExceededInputLengthIndexes
                    }
                />
            </Stack>
            <FormFooter>
                <Flex
                    justifyContent="end"
                    alignItems="center"
                    xcss={footerStyles}
                >
                    <div
                        css={[
                            modalVisualUpdates && submitButtonVisualUpdateCss
                        ]}
                    >
                        <LoadingButton
                            appearance="primary"
                            onClick={() => {
                                onClickFinishButton({
                                    onClickNext: onClickNextWrapper,
                                    columnNames,
                                    setEmptyInputIndexes,
                                    setDupeIndexes,
                                    setExceededInputLengthIndexes
                                });
                            }}
                            isLoading={isBoardColumnsSubmitting}
                            isDisabled={isBoardColumnsSubmitting}
                        >
                            {buttonMsg}
                        </LoadingButton>
                    </div>
                </Flex>
            </FormFooter>
        </>
    );
};

const subtextStyles = xcss({
    fontSize: "font.body.medium",
    color: "color.text.subtle"
});

const footerStyles = xcss({
    width: "100%",
    color: "color.text.subtlest"
});

// Visual update styles for cfind4831ProductOverlayModalVisualUpdates gate
const titleVisualUpdateCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h1: {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 700,
        fontSize: "32px",
        lineHeight: "38px"
    }
});

const subtextVisualUpdateStyles = css({
    font: token("font.body"),
    color: token("color.text.subtle"),
    margin: 0
});

const submitButtonVisualUpdateCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        paddingBlock: "6px !important",
        paddingInline: "12px !important",
        margin: "0 !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontFamily: wacToken("font.charlie.text") + " !important",
        fontSize: "14px !important",
        lineHeight: "1.5rem !important",
        fontWeight: `${token("font.weight.medium")} !important`,
        textAlign: "center",
        margin: "0 !important"
    }
});
